import type { TableSortValue } from "@src/shared/constants/sort-order.constants";
import apiService from "./api";
import { sanitizeQueryParams } from "@src/utility/helpers/apiHelper";
import { APP_UPDATE_STATUS, type AppUpdateStatusType } from "@src/views/apps/app-update/constants";

export { APP_UPDATE_STATUS, type AppUpdateStatusType };

export interface AppUpdateCategoryListItem {
  rowId: number;
  id: number;
  name: string;
  color: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppUpdateCategoryOption {
  id: number;
  name: string;
  color: string | null;
}

export interface CreateAppUpdateCategoryPayload {
  name: string;
  color: string;
}

export interface UpdateAppUpdateCategoryPayload {
  name?: string;
  color?: string | null;
}

export interface GetAppUpdateCategoriesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: TableSortValue;
}

export interface AppUpdateListItem {
  rowId: number;
  id: number;
  categoryId: number;
  categoryName?: string;
  categoryColor?: string | null;
  title: string;
  body: string;
  tags?: string[];
  status: AppUpdateStatusType;
  isPinned: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AppUpdateDetail {
  id: number;
  categoryId: number;
  categoryName?: string;
  categoryColor?: string | null;
  title: string;
  body: string;
  tags?: string[];
  status: AppUpdateStatusType;
  isPinned: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAppUpdatePayload {
  categoryId: number;
  title: string;
  body: string;
  tags?: string[];
  status?: AppUpdateStatusType;
  isPinned?: boolean;
  displayOrder?: number;
}

export interface UpdateAppUpdatePayload {
  categoryId?: number;
  title?: string;
  body?: string;
  tags?: string[];
  status?: AppUpdateStatusType;
  isPinned?: boolean;
  displayOrder?: number;
}

export interface GetAppUpdatesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: AppUpdateStatusType | "";
  categoryId?: number;
  orderBy?: string;
  orderDirection?: TableSortValue;
}


export class AppUpdateCategoryService {
  async getCategories(query?: GetAppUpdateCategoriesParams) {
    return apiService.get("/app-update-categories", {
      params: sanitizeQueryParams(query || {}),
    });
  }

  async getAllCategories() {
    return apiService.get("/app-update-categories/dropdown");
  }

  async getCategoryById(id: string | number) {
    return apiService.get(`/app-update-categories/${id}`);
  }

  async createCategory(data: CreateAppUpdateCategoryPayload) {
    return apiService.post("/app-update-categories", data);
  }

  async updateCategory(
    id: string | number,
    data: UpdateAppUpdateCategoryPayload
  ) {
    return apiService.put(`/app-update-categories/${id}`, data);
  }

  async deleteCategory(id: string | number) {
    return apiService.delete(`/app-update-categories/${id}`);
  }
}

export class AppUpdateService {
  async getAppUpdates(query?: GetAppUpdatesParams) {
    return apiService.get("/app-updates", {
      params: sanitizeQueryParams(query || {}),
    });
  }

  async getAppUpdateById(id: string | number) {
    return apiService.get(`/app-updates/${id}`);
  }

  async createAppUpdate(data: CreateAppUpdatePayload) {
    return apiService.post("/app-updates", data);
  }

  async updateAppUpdate(id: string | number, data: UpdateAppUpdatePayload) {
    return apiService.put(`/app-updates/${id}`, data);
  }

  async deleteAppUpdate(id: string | number) {
    return apiService.delete(`/app-updates/${id}`);
  }

  async getNextDisplayOrder() {
    return apiService.get("/app-updates/next-display-order");
  }
}

export const appUpdateCategoryService = new AppUpdateCategoryService();
export const appUpdateService = new AppUpdateService();
